import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Confetti from 'react-confetti';
import { useWindowSize } from 'react-use';
import { Trophy, Star, Zap, Calendar, Target, Crown } from 'lucide-react';
import { Button } from '@/components/ui/button';

const CHALLENGE_ICONS = {
  daily: Target,
  weekly: Calendar,
  monthly: Trophy
};

const CHALLENGE_COLORS = {
  daily: {
    gradient: 'from-blue-500 to-cyan-500',
    text: 'text-blue-500',
    bg: 'bg-blue-500'
  },
  weekly: {
    gradient: 'from-purple-500 to-pink-500',
    text: 'text-purple-500',
    bg: 'bg-purple-500'
  },
  monthly: {
    gradient: 'from-yellow-500 to-orange-500',
    text: 'text-yellow-500',
    bg: 'bg-yellow-500'
  }
};

const ChallengeCompletionPopup = ({ 
  isOpen, 
  onClose, 
  challengeType = 'daily',
  challengeTitle = 'Challenge',
  xpEarned = 0,
  levelUp = false,
  newLevel = null,
  isSpanish = false
}) => {
  const { width, height } = useWindowSize();
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShowConfetti(true);
      // Stop confetti after 5 seconds
      const timer = setTimeout(() => setShowConfetti(false), 5000);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const Icon = CHALLENGE_ICONS[challengeType] || Trophy;
  const colors = CHALLENGE_COLORS[challengeType] || CHALLENGE_COLORS.daily;

  const getMessage = () => {
    if (challengeType === 'daily') {
      return isSpanish ? '¡Desafío Diario Completado!' : 'Daily Challenge Complete!';
    } else if (challengeType === 'weekly') {
      return isSpanish ? '¡Desafío Semanal Completado!' : 'Weekly Challenge Complete!';
    } else if (challengeType === 'monthly') {
      return isSpanish ? '¡Desafío Mensual Completado!' : 'Monthly Challenge Complete!';
    }
    return isSpanish ? '¡Desafío Completado!' : 'Challenge Complete!';
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Confetti */}
          {showConfetti && (
            <Confetti
              width={width}
              height={height}
              recycle={false}
              numberOfPieces={300}
              gravity={0.2}
              colors={['#FFD700', '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FFEAA7', '#DDA0DD']}
            />
          )}

          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={onClose}
          >
            {/* Modal */}
            <motion.div
              initial={{ scale: 0.5, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 20 }}
              transition={{ type: "spring", damping: 15, stiffness: 300 }}
              className="bg-background rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
              onClick={e => e.stopPropagation()}
            >
              {/* Header with gradient */}
              <div className={`bg-gradient-to-r ${colors.gradient} p-6 text-white text-center`}>
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                  className="w-20 h-20 mx-auto mb-4 bg-white/20 rounded-full flex items-center justify-center"
                >
                  <Icon className="w-10 h-10" />
                </motion.div>
                
                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="text-2xl font-bold mb-2"
                >
                  {getMessage()}
                </motion.h2>
                
                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="text-white/90"
                >
                  {challengeTitle}
                </motion.p>
              </div>

              {/* Content */}
              <div className="p-6 space-y-4">
                {/* XP Earned */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.5 }}
                  className="flex items-center justify-center gap-3 py-4 bg-muted/50 rounded-xl"
                >
                  <Zap className="w-8 h-8 text-yellow-500" />
                  <div className="text-center">
                    <motion.span
                      initial={{ scale: 0 }}
                      animate={{ scale: [0, 1.3, 1] }}
                      transition={{ delay: 0.6, duration: 0.5 }}
                      className="text-3xl font-bold text-yellow-500"
                    >
                      +{xpEarned}
                    </motion.span>
                    <span className="text-xl font-bold text-yellow-500 ml-1">XP</span>
                  </div>
                </motion.div>

                {/* Level Up */}
                {levelUp && newLevel && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.7 }}
                    className="flex items-center justify-center gap-3 py-4 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-xl border border-purple-500/30"
                  >
                    <Crown className="w-8 h-8 text-purple-500" />
                    <div className="text-center">
                      <p className="text-sm text-muted-foreground">
                        {isSpanish ? '¡Subiste de Nivel!' : 'Level Up!'}
                      </p>
                      <p className="text-2xl font-bold text-purple-500">
                        {isSpanish ? 'Nivel' : 'Level'} {newLevel}
                      </p>
                    </div>
                    <Crown className="w-8 h-8 text-purple-500" />
                  </motion.div>
                )}

                {/* Stars decoration */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.8 }}
                  className="flex justify-center gap-2"
                >
                  {[...Array(5)].map((_, i) => (
                    <motion.div
                      key={i}
                      initial={{ scale: 0, rotate: -45 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ delay: 0.8 + i * 0.1 }}
                    >
                      <Star className={`w-6 h-6 ${colors.text} fill-current`} />
                    </motion.div>
                  ))}
                </motion.div>

                {/* Close button */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 1 }}
                >
                  <Button 
                    onClick={onClose}
                    className={`w-full bg-gradient-to-r ${colors.gradient} hover:opacity-90 text-white`}
                    size="lg"
                  >
                    {isSpanish ? '¡Genial!' : 'Awesome!'}
                  </Button>
                </motion.div>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ChallengeCompletionPopup;
